/**
 * Tool view component.
 * @module components/View/ToolView
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Confirm,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { filter } from 'lodash';
import { Link, useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { deleteContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

import leftKeySVG from '@plone/volto/icons/left-key.svg';
import addSVG from '@plone/volto/icons/add.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';
import pencilSVG from '@plone/volto/icons/pencil.svg';

// EUR has no live exchange rate of its own yet, so it's shown as a flat
// $5 markup over the USD price.
const formatPrice = (price, currency) => {
  if (!price) return 'N/A';
  return currency === 'EUR' ? `€ ${Number(price) + 5}` : `$ ${price}`;
};
const formatInventory = (inventory) => inventory ?? 0;

const fullcode = (content) =>
  content.code_variant
    ? `${content.code}.${content.code_variant}`
    : content.code;

const fullsize = (content) =>
  content.size_variant
    ? `${content.size}${content.size_variant}`
    : content.size;

/**
 * Tool view component class.
 * @function ToolView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const ToolView = ({ content }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const currency = useSelector((state) => state.currency);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const items = [...(content.items || [])];

  const parentUrl = flattenToAppURL(content['@id'])
    .split('/')
    .slice(0, -1)
    .join('/');

  const byCode = (a, b) =>
    fullsize(a).localeCompare(fullsize(b), undefined, { numeric: true });

  const sets = filter(items, (item) => item['@type'] === 'Set').sort(byCode);
  const sizes = filter(items, (item) => item['@type'] === 'Size').sort(byCode);

  const onDelete = async () => {
    await dispatch(deleteContent(flattenToAppURL(content['@id'])));
    setShowDeleteConfirm(false);
    history.push('/tools');
  };

  return (
    <>
      <div className="button-toolbar">
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => history.push(parentUrl)}
          aria-label="Back"
        >
          <Icon name={leftKeySVG} size="24px" />
        </button>
        <Link
          className="circular-button float-left"
          to={`${flattenToAppURL(content['@id'])}/edit`}
        >
          <Icon name={pencilSVG} size="24px" />
        </Link>
        <button
          type="button"
          className="circular-button float-left"
          onClick={() => setShowDeleteConfirm(true)}
        >
          <Icon name={deleteSVG} size="24px" />
        </button>
        <div className="button-toolbar-title">
          {content.category ? content.category : ''}
        </div>
      </div>
      <Confirm
        open={showDeleteConfirm}
        header="Delete tool"
        content={`Are you sure you want to delete "${content.title}"?`}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete}
      />
      <div className="view-padding">
        <article id="tool">
          <h1 className="content-title">{content.title}</h1>
          <Table celled selectable>
            <TableHeader>
              <TableRow>
                <TableHeaderCell
                  colspan="4"
                  textAlign="center"
                  className="main buttons"
                >
                  <span className="th-title">Sets</span>
                  <Link
                    className="circular-button float-right"
                    to={`${flattenToAppURL(content['@id'])}/add?type=Set`}
                  >
                    <Icon name={addSVG} size="24px" />
                  </Link>
                </TableHeaderCell>
              </TableRow>
              {sets.length > 0 && (
                <TableRow>
                  <TableHeaderCell width="3">Code</TableHeaderCell>
                  <TableHeaderCell>Tools</TableHeaderCell>
                  <TableHeaderCell width="3">Inventory</TableHeaderCell>
                  <TableHeaderCell width="3">Price</TableHeaderCell>
                </TableRow>
              )}
            </TableHeader>
            {sets.length > 0 && (
              <TableBody>
                {sets.map((item) => (
                  <TableRow
                    className="clickable-row"
                    onClick={() => history.push(flattenToAppURL(item['@id']))}
                  >
                    <TableCell width="3">
                      {fullcode(content)}
                      {fullsize(item)}
                    </TableCell>
                    <TableCell>{item.set.split('\n').join(', ')}</TableCell>
                    <TableCell width="3">
                      {formatInventory(item.inventory)}
                    </TableCell>
                    <TableCell width="3">
                      {formatPrice(item.price, currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          <Table celled selectable>
            <TableHeader>
              <TableRow>
                <TableHeaderCell
                  colspan="4"
                  textAlign="center"
                  className="main buttons"
                >
                  <span className="th-title">Sizes</span>
                  <Link
                    className="circular-button float-right"
                    to={`${flattenToAppURL(content['@id'])}/add?type=Size`}
                  >
                    <Icon name={addSVG} size="24px" />
                  </Link>
                </TableHeaderCell>
              </TableRow>
              {sizes.length > 0 && (
                <TableRow>
                  <TableHeaderCell width="3">Code</TableHeaderCell>
                  <TableHeaderCell>Sold separate</TableHeaderCell>
                  <TableHeaderCell width="3">Inventory</TableHeaderCell>
                  <TableHeaderCell width="3">Price</TableHeaderCell>
                </TableRow>
              )}
            </TableHeader>
            {sizes.length > 0 && (
              <TableBody>
                {sizes.map((item) => (
                  <TableRow
                    className="clickable-row"
                    onClick={() => history.push(flattenToAppURL(item['@id']))}
                  >
                    <TableCell width="3">
                      {fullcode(content)}
                      {fullsize(item)}
                    </TableCell>
                    <TableCell>{item.sold_separate ? 'Yes' : 'No'}</TableCell>
                    <TableCell width="3">
                      {formatInventory(item.inventory)}
                    </TableCell>
                    <TableCell width="3">
                      {formatPrice(item.price, currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </article>
      </div>
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
ToolView.propTypes = {
  content: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    category: PropTypes.string,
  }).isRequired,
};

export default ToolView;
